import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import * as api from '../services/api';
import { 
  Monitor, 
  Smartphone, 
  CheckCircle, 
  ChevronRight, 
  ArrowLeft,
  Mail,
  User,
  Zap
} from 'lucide-react';

const KioskModePage = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [step, setStep] = useState<'welcome' | 'form' | 'success'>('welcome');
    const [formData, setFormData] = useState({ firstName: '', email: '', preferredChannel: 'EMAIL' as const });
    
    const { data: event } = useQuery({
        queryKey: ['event', eventId],
        queryFn: () => api.getEvent(eventId!),
        enabled: !!eventId
    });

    const joinMutation = useMutation({
        mutationFn: (data: any) => api.joinQueue({ ...data, eventId: event?.id, organizationId: event?.organizationId }),
        onSuccess: () => setStep('success')
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        joinMutation.mutate(formData);
    };

    if (step === 'success') {
        return (
            <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col items-center justify-center p-8 text-center animate-in zoom-in duration-500">
                <div className="w-32 h-32 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center mb-8">
                    <CheckCircle className="w-16 h-16" />
                </div>
                <h1 className="text-5xl font-black text-gray-900 dark:text-white tracking-tighter mb-4 italic uppercase">C'est fait !</h1>
                <p className="text-xl text-gray-500 dark:text-gray-400 font-medium max-w-lg mb-12">
                    Vous êtes maintenant dans la file. Vous allez recevoir une notification sur votre <span className="text-blue-600 font-bold">téléphone</span> dès que c'est votre tour.
                </p>
                <button 
                    onClick={() => { setStep('welcome'); setFormData({ firstName: '', email: '', preferredChannel: 'EMAIL' }); }}
                    className="px-10 py-5 bg-gray-900 dark:bg-white dark:text-gray-950 text-white rounded-2xl font-black uppercase tracking-widest text-sm hover:scale-105 active:scale-95 transition-all shadow-2xl"
                >
                    Revenir à l'accueil
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex flex-col items-center justify-center p-6 relative overflow-hidden transition-colors">
            
            {/* Background Decor */}
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none select-none">
                <div className="text-[20rem] font-black uppercase tracking-tighter absolute -top-20 -left-20 rotate-12">{event?.name}</div>
            </div>

            <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-[3rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)] dark:shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] border dark:border-slate-800 overflow-hidden relative z-10">
                
                {step === 'welcome' && (
                    <div className="p-12 sm:p-20 text-center animate-in slide-in-from-bottom-8 duration-700">
                        <div className="w-20 h-20 bg-blue-600 text-white rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl shadow-blue-600/30">
                            <Zap className="w-10 h-10" />
                        </div>
                        <h2 className="text-[10px] font-black text-blue-600 uppercase tracking-[0.3em] mb-4">Bienvenue au salon</h2>
                        <h1 className="text-5xl font-black text-gray-900 dark:text-white tracking-tighter mb-6 leading-[0.95] italic uppercase">{event?.name}</h1>
                        <p className="text-gray-500 dark:text-gray-400 text-lg font-medium mb-12">Envie d'un flash ou d'une session ? Rejoignez la file d'attente digitale en quelques secondes.</p>
                        
                        <button 
                            onClick={() => setStep('form')}
                            className="w-full py-6 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest text-lg flex items-center justify-center gap-3 hover:bg-blue-700 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-blue-600/20"
                        >
                            Commencer l'inscription <ChevronRight className="w-6 h-6" />
                        </button>

                        <div className="mt-8 pt-8 border-t dark:border-slate-800 flex items-center justify-center gap-6 text-gray-400">
                             <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest italic">
                                 <Monitor className="w-4 h-4" /> Mode Tablette
                             </div>
                             <div className="w-1 h-1 bg-gray-300 rounded-full" />
                             <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest italic">
                                 <Smartphone className="w-4 h-4" /> Notification Mobile
                             </div>
                        </div>
                    </div>
                )}

                {step === 'form' && (
                    <div className="p-10 sm:p-16 animate-in slide-in-from-right-8 duration-500">
                        <button onClick={() => setStep('welcome')} className="text-gray-400 hover:text-gray-600 mb-8 flex items-center gap-2 font-black text-[10px] uppercase tracking-widest transition-colors">
                            <ArrowLeft className="w-4 h-4" /> Retour
                        </button>
                        
                        <h3 className="text-3xl font-black text-gray-900 dark:text-white tracking-tighter mb-8 italic uppercase">Vos Informations</h3>
                        
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                    <User className="w-5 h-5" />
                                </div>
                                <input 
                                    type="text"
                                    required
                                    placeholder="Prénom"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                                    className="w-full pl-12 pr-4 py-5 bg-gray-50 dark:bg-slate-950 border dark:border-slate-800 rounded-2xl text-lg focus:ring-4 focus:ring-blue-600/10 focus:border-blue-600 outline-none dark:text-white transition-all font-bold placeholder:font-medium"
                                />
                            </div>

                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                    <Mail className="w-5 h-5" />
                                </div>
                                <input 
                                    type="email"
                                    required
                                    placeholder="E-mail (pour votre ticket digital)"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    className="w-full pl-12 pr-4 py-5 bg-gray-50 dark:bg-slate-950 border dark:border-slate-800 rounded-2xl text-lg focus:ring-4 focus:ring-blue-600/10 focus:border-blue-600 outline-none dark:text-white transition-all font-bold placeholder:font-medium"
                                />
                            </div>

                            <button 
                                type="submit"
                                disabled={joinMutation.isPending}
                                className="w-full py-6 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest text-lg hover:bg-blue-700 active:scale-[0.98] transition-all shadow-xl shadow-blue-600/20 disabled:opacity-50 mt-4"
                            >
                                {joinMutation.isPending ? "Inscription..." : "Valider mon ticket"}
                            </button>
                        </form>
                    </div>
                )}
            </div>
            
            <p className="mt-8 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] animate-pulse">QueueFlow Kiosk System v1.0</p>
        </div>
    );
};

export default KioskModePage;
